import { Controller } from "@hotwired/stimulus"

// Preview switcher on /features/website-builder.
//
// Two independent axes:
//   - LAYOUT (tab): Bold | Editorial — structural, no color
//   - SCHEME (swatch): one of 12 named color presets (6 light + 6 dark)
//     Each scheme sets 7 CSS variables on `frame`, scoped to the preview:
//       --bg, --surface, --text, --text-muted, --border, --brand-bg, --brand-fg
//
// Every styled element inside the panels reads these vars, so changing
// the scheme recolors both Bold and Editorial layouts consistently.
//
// Scheme presets match the App Builder (/tools/app-preview) so a gym
// owner sees the same palette across products.
export default class extends Controller {
  static targets = ["tab", "panel", "swatch", "frame"]

  connect() {
    const selectedTab = this.tabTargets.find((t) => t.getAttribute("aria-selected") === "true")
    const tabKey = selectedTab?.dataset.previewKey || this.tabTargets[0]?.dataset.previewKey
    if (tabKey) this.showPanel(tabKey)

    const selectedSwatch = this.swatchTargets.find((s) => s.getAttribute("aria-pressed") === "true")
    if (selectedSwatch) this.applyScheme(selectedSwatch)
  }

  select(event) {
    const key = event.currentTarget.dataset.previewKey
    if (key) this.showPanel(key)
  }

  swatch(event) {
    this.applyScheme(event.currentTarget)
  }

  showPanel(key) {
    this.tabTargets.forEach((btn) => {
      const on = btn.dataset.previewKey === key
      btn.setAttribute("aria-selected", on ? "true" : "false")
      btn.classList.toggle("bg-white", on)
      btn.classList.toggle("text-gray-900", on)
      btn.classList.toggle("shadow-sm", on)
      btn.classList.toggle("text-gray-400", !on)
    })

    this.panelTargets.forEach((panel) => {
      const on = panel.dataset.previewKey === key
      panel.classList.toggle("hidden", !on)
    })
  }

  applyScheme(swatch) {
    if (!this.hasFrameTarget) return

    const vars = {
      "--bg":         swatch.dataset.bg,
      "--surface":    swatch.dataset.surface,
      "--text":       swatch.dataset.text,
      "--text-muted": swatch.dataset.textMuted,
      "--border":     swatch.dataset.border,
      "--brand-bg":   swatch.dataset.brandBg,
      "--brand-fg":   swatch.dataset.brandFg,
    }
    Object.entries(vars).forEach(([k, v]) => {
      if (v) this.frameTarget.style.setProperty(k, v)
    })

    this.swatchTargets.forEach((s) => {
      const on = s === swatch
      s.setAttribute("aria-pressed", on ? "true" : "false")
      s.classList.toggle("ring-2", on)
      s.classList.toggle("ring-white", on)
    })
  }
}
